import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdmin } from '../hooks/useAdmin';
import { useTranslation } from 'react-i18next';
import { Users, Settings, LogOut, Info, Shield, Plus, Check, X, UserX, ArrowLeft, Copy, RefreshCw, HeartHandshake, FileSpreadsheet } from 'lucide-react';
import { gradientText } from '../theme';
import LoadingSpinner from '../components/LoadingSpinner';
import { supabase } from '../lib/supabase';
import { useAuthContext } from '../contexts/AuthContext';
import { useAppSettings } from '../hooks/useAppSettings';
import { useCachedData } from '../hooks/useCachedData';

// Définition des types pour les sections du tableau de bord
type Section = {
  id: string;
  icon: React.ReactNode;
  title: string;
  requiredPermission: string;
  path: string;
};

interface StatsData {
  totalUsers?: number;
  activeUsers?: number;
  totalRelations?: number;
  certifiedRelations?: number;
  payments?: {
    totalTransactions: number;
    totalAmount?: number; // Nouveau champ pour le montant total
    byAmount: Record<string, number>;
  };
  totalReferrals?: number;
  referralEarnings?: number;
  lastPaymentDate?: string;
  totalPaymentsReceived?: number;
}

// Type pour les administrateurs
type AdminUserItem = {
  user_id: string;
  email: string;
  role_name: string;
  assigned_at: string;
};

const AdminDashboard: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isAdmin, adminRole, hasPermission, loading } = useAdmin();
  const { user } = useAuthContext();
  const [stats, setStats] = useState<StatsData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showAddAdminForm, setShowAddAdminForm] = useState(false);
  const [newAdminEmail, setNewAdminEmail] = useState('');
  const [newAdminRole, setNewAdminRole] = useState('admin');
  const [addingAdmin, setAddingAdmin] = useState(false);
  const [addAdminError, setAddAdminError] = useState<string | null>(null);
  const [addAdminSuccess, setAddAdminSuccess] = useState(false);
  const [adminsList, setAdminsList] = useState<AdminUserItem[]>([]);
  const [loadingAdmins, setLoadingAdmins] = useState(false);
  const [revokingAdmin, setRevokingAdmin] = useState<string | null>(null);
  const [viewingAdminId, setViewingAdminId] = useState<string | null>(null);
  const [viewingAdminEmail, setViewingAdminEmail] = useState<string>('');
  const [adminReferrals, setAdminReferrals] = useState<number>(0);
  const [adminEarnings, setAdminEarnings] = useState<number>(0);
  const [adminTotalPaymentsReceived, setAdminTotalPaymentsReceived] = useState<number>(0);
  const [loadingAdminStats, setLoadingAdminStats] = useState(false);
  const [referralLink, setReferralLink] = useState<string>('');
  const [referralLinkLoading, setReferralLinkLoading] = useState(false);
  const [referralCopied, setReferralCopied] = useState(false);
  const [fullName, setFullName] = useState<string>('');
  const [mobileMoneyNumber, setMobileMoneyNumber] = useState<string>('');
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [adminFullName, setAdminFullName] = useState<string>('');
  const [adminMobileNumber, setAdminMobileNumber] = useState<string>('');
  const [adminPaymentStatus, setAdminPaymentStatus] = useState<string>('pending');
  const [adminLastPaymentDate, setAdminLastPaymentDate] = useState<string | null>(null);
  const [validatingPayment, setValidatingPayment] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const { getReferralCommissionRate } = useAppSettings();
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredAdminsList, setFilteredAdminsList] = useState<AdminUserItem[]>([]);
  const [adminProfiles, setAdminProfiles] = useState<Record<string, any>>({});
  const [resettingTransactions, setResettingTransactions] = useState(false);
  const [transactionResetSuccess, setTransactionResetSuccess] = useState(false);
  const [transactionResetError, setTransactionResetError] = useState<string | null>(null);
  // Ajout d'un référence pour suivre les intervalles
  const autoRefreshIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Vérifier si l'utilisateur est super admin
  const isSuperAdmin = adminRole?.name === 'super_admin';

  // Définir les sections disponibles dans le tableau de bord
  const sections: Section[] = isSuperAdmin ? [
    {
      id: 'users',
      icon: <Users className="w-5 h-5" />,
      title: t('admin.dashboard.sections.users'),
      requiredPermission: 'users.view',
      path: '/admin/users'
    },
    {
      id: 'settings',
      icon: <Settings className="w-5 h-5" />,
      title: t('admin.dashboard.sections.settings'),
      requiredPermission: 'settings.view',
      path: '/admin/settings'
    }
  ] : [
    {
      id: 'referrals',
      icon: <Users className="w-5 h-5" />,
      title: "Mes parrainages",
      requiredPermission: 'users.view',
      path: '/admin/referrals'
    }
  ];

  // Filtrer les sections accessibles en fonction des permissions de l'utilisateur
  const accessibleSections = React.useMemo(() => {
    if (!isAdmin) return [];
    
    // Pour les admins standards, ne pas afficher de sections
    if (!isSuperAdmin) {
      return []; // Aucune section pour les admins standards
    }
    
    return sections.filter(section => {
      try {
        return hasPermission(section.requiredPermission);
      } catch (error) {
        console.error(`Erreur lors de la vérification de la permission ${section.requiredPermission}:`, error);
        return false;
      }
    });
  }, [isAdmin, hasPermission, sections, isSuperAdmin]);
  
  // Liens principaux du tableau de bord
  const dashboardLinks = React.useMemo(() => {
    const links = [
      {
        id: 'users',
        title: t('admin.dashboard.links.users', 'Utilisateurs'),
        description: t('admin.dashboard.links.usersDesc', 'Gérer les utilisateurs de l\'application'),
        icon: <Users className="w-6 h-6 text-blue-500" />,
        link: '/admin/users',
        requiredPermission: 'users.view'
      },
      // Lien Relations - n'apparaît pas pour les super_admin
      ...(!isSuperAdmin ? [{
        id: 'relations',
        title: t('admin.dashboard.links.relations', 'Relations'),
        description: t('admin.dashboard.links.relationsDesc', 'Gérer les relations entre utilisateurs'),
        icon: <HeartHandshake className="w-6 h-6 text-pink-500" />,
        link: '/admin/relations',
        requiredPermission: 'relations.view'
      }] : []),
      {
        id: 'settings',
        title: t('admin.dashboard.links.settings', 'Paramètres'),
        description: t('admin.dashboard.links.settingsDesc', 'Configurer les paramètres de l\'application'),
        icon: <Settings className="w-6 h-6 text-yellow-500" />,
        link: '/admin/settings',
        requiredPermission: 'settings.view'
      }
    ];
    
    // Ajouter le lien vers les annonces uniquement pour les super admin
    if (isSuperAdmin) {
      links.push({
        id: 'annonces',
        title: 'Annonces',
        description: 'Gérer les annonces de profils',
        icon: <FileSpreadsheet className="w-6 h-6 text-purple-500" />,
        link: '/admin/annonces',
        requiredPermission: '' // Pas de permission requise, contrôlé par le composant
      });
    }
    
    // Filtrer les liens en fonction des permissions
    return links.filter(link => {
      if (!link.requiredPermission) return true;
      try {
        return hasPermission(link.requiredPermission);
      } catch (error) {
        console.error(`Erreur lors de la vérification de la permission ${link.requiredPermission}:`, error);
        return false;
      }
    });
  }, [t, isSuperAdmin, hasPermission]);
  
  // Ajouter ce code dans la partie rendu du composant, juste après les sections
  const renderDashboardLinks = () => {
    return (
      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 dashboard-links">
        {dashboardLinks.map(link => (
          <div 
            key={link.id}
            className="bg-gray-900/50 border border-gray-800 rounded-xl p-4 hover:bg-gray-900 transition-colors cursor-pointer"
            onClick={() => navigate(link.link)}
          >
            <div className="flex items-start">
              <div className="p-2 rounded-lg bg-gray-800 mr-3">
                {link.icon}
              </div>
              <div>
                <h3 className="font-semibold text-white">{link.title}</h3>
                <p className="text-sm text-gray-400">{link.description}</p>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  // Utiliser useCachedData pour les statistiques du super admin
  const fetchSuperAdminStats = useCallback(async () => {
    console.log('[AdminDashboard] Début du chargement des statistiques');
    
    const statsData: StatsData = {};
    let hasAtLeastOneStat = false;

    if (isSuperAdmin) {
      // Statistiques pour super admin
      try {
        const { count: usersCount, error: usersError } = await supabase
          .from('profiles')
          .select('*', { count: 'exact', head: true });
        
        if (usersError) {
          console.error('[AdminDashboard] Erreur lors du comptage des utilisateurs:', usersError);
        } else if (usersCount !== null) {
          statsData.totalUsers = usersCount;
          
          // Récupérer le nombre de transactions non archivées
          try {
            const { count: transactionsCount, error: transactionsError } = await supabase
              .from('transaction_amounts')
              .select('*', { count: 'exact', head: true })
              .not('user_id', 'is', null)  // Seulement les transactions utilisées
              .or('is_archived.is.null,is_archived.eq.false');    // Non archivées
            
            // Récupérer le montant total des transactions
            const { data: transactionData, error: dataError } = await supabase
              .from('transaction_amounts')
              .select('montant')
              .not('user_id', 'is', null)  // Seulement les transactions utilisées
              .or('is_archived.is.null,is_archived.eq.false');    // Non archivées
            
            if (transactionsError || dataError) {
              console.error('[AdminDashboard] Erreur lors du comptage des transactions:', 
                transactionsError || dataError);
              statsData.payments = {
                totalTransactions: 0,
                totalAmount: 0,
                byAmount: {}
              };
            } else {
              // Calculer le montant total
              const totalAmount = transactionData?.reduce((sum, t) => sum + (Number(t.montant) || 0), 0) || 0;
              
              statsData.payments = {
                totalTransactions: transactionsCount || 0,
                totalAmount: totalAmount,
                byAmount: {}
              };
              console.log('[AdminDashboard] Nombre de transactions récupéré:', transactionsCount);
              console.log('[AdminDashboard] Montant total récupéré:', totalAmount);
            }
          } catch (err) {
            console.error('[AdminDashboard] Exception lors du comptage des transactions:', err);
            statsData.payments = {
              totalTransactions: 0,
              totalAmount: 0,
              byAmount: {}
            };
          }
          
          hasAtLeastOneStat = true;
        }
      } catch (err) {
        console.error('[AdminDashboard] Exception lors du comptage des utilisateurs:', err);
      }
    }
    
    return hasAtLeastOneStat ? statsData : null;
  }, [isSuperAdmin]);
  
  // Utiliser useCachedData pour les statistiques de l'admin standard
  const fetchAdminStats = useCallback(async () => {
    if (!user) return null;
    
    try {
      // 1. Récupérer le nombre de parrainages
      const { count: referralCount, error: referralError } = await supabase
        .from('referrals')
        .select('*', { count: 'exact', head: true })
        .eq('admin_id', user.id);
      
      if (referralError) {
        console.error('[AdminDashboard] Erreur lors du comptage des parrainages:', referralError);
        return null;
      }
      
      // 2. Récupérer le montant total des commissions - utiliser sum() côté serveur est plus fiable
      const { data: earningsData, error: earningsError } = await supabase
        .rpc('get_total_referral_earnings', { admin_uuid: user.id });
      
      // 3. Récupérer total_payments_received et last_payment_date
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('total_payments_received, last_payment_date')
        .eq('id', user.id)
        .single();
      
      if (profileError) {
        console.error('[AdminDashboard] Erreur lors de la récupération des historiques de paiement:', profileError);
      }
      
      if (earningsError) {
        console.error('[AdminDashboard] Erreur lors de la récupération des commissions:', earningsError);
        
        // Méthode de secours en cas d'erreur RPC: récupérer et additionner manuellement
        const { data: fallbackData, error: fallbackError } = await supabase
          .from('referrals')
          .select('amount')
          .eq('admin_id', user.id)
          .neq('amount', 0); // Exclure les montants nuls pour optimiser
        
        if (fallbackError) {
          console.error('[AdminDashboard] Erreur lors de la récupération de secours des commissions:', fallbackError);
          return null;
        }
        
        // Calculer le total des commissions manuellement
        const totalEarnings = fallbackData?.reduce((sum, item) => {
          const amount = parseFloat(item.amount);
          return sum + (isNaN(amount) ? 0 : amount);
        }, 0) || 0;
        
        console.log('[AdminDashboard] Commissions calculées manuellement:', totalEarnings);
        
        // Retourner les statistiques
        return {
          totalReferrals: referralCount || 0,
          referralEarnings: totalEarnings,
          totalPaymentsReceived: parseFloat(profileData?.total_payments_received) || 0,
          lastPaymentDate: profileData?.last_payment_date || null
        };
      } else {
        // Utiliser le résultat de la fonction RPC
        const totalEarnings = earningsData || 0;
        console.log('[AdminDashboard] Commissions récupérées par RPC:', totalEarnings);
        
        // Retourner les statistiques
        return {
          totalReferrals: referralCount || 0,
          referralEarnings: totalEarnings,
          totalPaymentsReceived: parseFloat(profileData?.total_payments_received) || 0,
          lastPaymentDate: profileData?.last_payment_date || null
        };
      }
    } catch (err) {
      console.error('[AdminDashboard] Erreur lors du chargement des statistiques:', err);
      return null;
    }
  }, [user]);
  
  // Utilisation de useCachedData pour les statistiques
  const statsCacheKey = isSuperAdmin 
    ? `admin_dashboard_stats_superadmin_${user?.id}` 
    : `admin_dashboard_stats_admin_${user?.id}`;
  
  const fetchFunction = isSuperAdmin ? fetchSuperAdminStats : fetchAdminStats;
  
  const { 
    data: cachedStats, 
    isLoading: cachedLoading, 
    error: cachedError, 
    refresh: refreshCachedStats,
    invalidateCache
  } = useCachedData<StatsData | null>(
    statsCacheKey,
    fetchFunction,
    30000, // Cache de 30 secondes
    [isSuperAdmin, user?.id] // Dépendances
  );
  
  // Mettre à jour les stats locales quand les données cachées changent
  useEffect(() => {
    if (cachedStats) {
      setStats(cachedStats);
    }
    if (cachedError) {
      setError(cachedError.message);
    }
  }, [cachedStats, cachedLoading, cachedError]);

  // Configurer une actualisation automatique
  useEffect(() => {
    if (!isAdmin) return;
    
    // Nettoyer l'intervalle existant si présent
    if (autoRefreshIntervalRef.current) {
      clearInterval(autoRefreshIntervalRef.current);
    }
    
    // Créer un nouvel intervalle d'actualisation
    autoRefreshIntervalRef.current = setInterval(() => {
      console.log('[AdminDashboard] Actualisation automatique des statistiques');
      refreshCachedStats();
    }, 60000); // Actualiser toutes les minutes
    
    return () => {
      if (autoRefreshIntervalRef.current) {
        clearInterval(autoRefreshIntervalRef.current);
      }
    };
  }, [isAdmin, refreshCachedStats]);
  
  // Écouter les mises à jour de transaction_amounts
  useEffect(() => {
    if (!isAdmin) return;
    
    // S'abonner aux changements de transactions
    const transactionsSubscription = supabase
      .channel('transaction_changes')
      .on('postgres_changes', { 
        event: '*', 
        schema: 'public', 
        table: 'transaction_amounts' 
      }, (payload) => {
        console.log('[AdminDashboard] Changement détecté dans les transactions:', payload);
        invalidateCache();
      })
      .subscribe();
    
    // S'abonner aux changements de referrals si c'est un admin standard
    let referralsSubscription: ReturnType<typeof supabase.channel> | undefined;
    if (!isSuperAdmin) {
      referralsSubscription = supabase
        .channel('referral_changes')
        .on('postgres_changes', { 
          event: '*', 
          schema: 'public', 
          table: 'referrals',
          filter: `admin_id=eq.${user?.id}`
        }, (payload) => {
          console.log('[AdminDashboard] Changement détecté dans les parrainages:', payload);
          invalidateCache();
        })
        .subscribe();
    }
    
    return () => {
      transactionsSubscription.unsubscribe();
      if (referralsSubscription) {
        referralsSubscription.unsubscribe();
      }
    };
  }, [isAdmin, isSuperAdmin, user?.id, invalidateCache]);

  // Mettre à jour la fonction refreshStats pour utiliser le cache
  const refreshStats = useCallback(async () => {
    setIsLoading(true);
    await refreshCachedStats();
    setIsLoading(false);
  }, [refreshCachedStats]);

  // Charger les informations du profil
  useEffect(() => {
    const loadProfile = async () => {
      if (!user) return;
      
      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('full_name, mobile_money_number')
          .eq('id', user.id)
          .single();
          
        if (error) throw error;
        
        if (data) {
          setFullName(data.full_name || '');
          setMobileMoneyNumber(data.mobile_money_number || '');
        }
      } catch (err) {
        console.error('Erreur lors du chargement du profil:', err);
      }
    };
    
    loadProfile();
  }, [user]);

  // Sauvegarder les modifications du profil
  const saveProfile = async () => {
    if (!user) return;
    
    try {
      setIsSaving(true);
      setProfileError(null);
      
      const { error } = await supabase
        .from('profiles')
        .update({
          full_name: fullName,
          mobile_money_number: mobileMoneyNumber
        })
        .eq('id', user.id);
        
      if (error) throw error;
      
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      console.error('Erreur lors de la sauvegarde du profil:', err);
      setProfileError(err instanceof Error ? err.message : 'Erreur lors de la sauvegarde');
    } finally {
      setIsSaving(false);
    }
  };

  // Ajouter une fonction pour valider le paiement
  const validatePayment = async () => {
    if (!user) return;
    
    // L'admin normal ne peut pas valider lui-même son paiement
    if (!isSuperAdmin) {
      setProfileError("Seul un super admin peut valider les paiements");
      setTimeout(() => setProfileError(null), 3000);
      return;
    }
    
    try {
      setIsSaving(true);
      setProfileError(null);
      
      // 1. Changer le statut de pending à validated
      const { error: updateError } = await supabase
        .from('profiles')
        .update({
          payment_status: 'validated'
        })
        .eq('id', user.id);
        
      if (updateError) throw updateError;
      
      // 2. Récupérer le montant total des commissions
      const currentEarnings = stats?.referralEarnings || 0;
      
      // 3. Enregistrer le paiement dans l'historique
      if (currentEarnings > 0) {
        const { error: logError } = await supabase
          .rpc('update_payment_info', {
            p_user_id: user.id,
            p_amount: currentEarnings,
            p_payment_date: new Date().toISOString()
          });
          
        if (logError) throw logError;
      }
      
      // 4. Remettre les statistiques à zéro
      const { error: resetError } = await supabase
        .from('referrals')
        .update({ amount: 0 })
        .eq('admin_id', user.id);
        
      if (resetError) throw resetError;
      
      // 5. Remettre le statut à pending
      const { error: statusError } = await supabase
        .from('profiles')
        .update({
          payment_status: 'pending'
        })
        .eq('id', user.id);
        
      if (statusError) throw statusError;
      
      // 6. Actualiser les statistiques
      invalidateCache();
      refreshStats();
      
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      console.error('Erreur lors de la validation du paiement:', err);
      setProfileError(err instanceof Error ? err.message : 'Erreur lors de la validation du paiement');
    } finally {
      setIsSaving(false);
    }
  };

  // Fonction pour réinitialiser le compteur de transactions
  const resetTransactionsCount = async () => {
    if (!isSuperAdmin || !user) return;
    
    try {
      setResettingTransactions(true);
      setTransactionResetError(null);
      setTransactionResetSuccess(false);
      
      // 1. Enregistrer l'historique de la réinitialisation
      const { error: historyError } = await supabase
        .from('transaction_reset_history')
        .insert({
          admin_id: user.id,
          count_before_reset: stats?.payments?.totalTransactions || 0,
          amount_before_reset: stats?.payments?.totalAmount || 0,
          reset_date: new Date().toISOString()
        });
      
      if (historyError) {
        console.error('[AdminDashboard] Erreur lors de l\'enregistrement de l\'historique:', historyError);
        throw historyError;
      }
      
      // 2. Archiver toutes les transactions validées
      const { data: resetData, error: resetError } = await supabase
        .rpc('archive_verified_transactions');
      
      if (resetError) {
        console.error('[AdminDashboard] Erreur lors de l\'archivage des transactions:', resetError);
        throw resetError;
      }
      
      console.log(`[AdminDashboard] ${resetData} transactions archivées avec succès`);
      
      // 3. Rafraîchir les statistiques
      invalidateCache();
      await refreshStats();
      
      setTransactionResetSuccess(true);
      setTimeout(() => setTransactionResetSuccess(false), 3000);
    } catch (err) {
      console.error('[AdminDashboard] Erreur lors de la réinitialisation:', err);
      setTransactionResetError(err instanceof Error ? err.message : 'Erreur inconnue');
    } finally {
      setResettingTransactions(false);
    }
  };

  // Charger la liste des administrateurs
  const loadAdminsList = async () => {
    if (!isSuperAdmin) return;
    
    try {
      setLoadingAdmins(true);
      
      // Ajouter un log pour déboguer
      console.log('[AdminDashboard] Chargement des administrateurs, searchTerm:', searchTerm);
      
      // Charger la liste des admins comme avant
      const { data, error } = await supabase.rpc('get_admin_users_with_roles');
      
      if (error) {
        console.error('Erreur lors du chargement des administrateurs:', error);
        
        // Méthode de secours si la RPC n'existe pas
        const { data: fallbackData, error: fallbackError } = await supabase
          .from('admin_users')
          .select(`
            user_id,
            role_id,
            admin_roles!inner(name),
            assigned_at
          `);
          
        if (fallbackError) {
          console.error('Erreur lors de la méthode de secours:', fallbackError);
          return;
        }
        
        // Traiter les données de la méthode de secours
        if (fallbackData && fallbackData.length > 0) {
          const userIds = fallbackData.map(admin => admin.user_id);
          
          const { data: profilesData, error: profilesError } = await supabase
            .from('profiles')
            .select('id, email')
            .in('id', userIds);
            
          if (profilesError) {
            console.error('Erreur lors de la récupération des emails:', profilesError);
            return;
          }
          
          // Combiner les données
          const adminsWithEmail = fallbackData.map(admin => {
            const profile = profilesData?.find(p => p.id === admin.user_id);
            
            // Récupérer directement le nom du rôle
            const roleName = typeof admin.admin_roles === 'object' && admin.admin_roles !== null 
              ? (admin.admin_roles as { name?: string }).name || 'Inconnu'
              : 'Inconnu';
            
            return {
              user_id: admin.user_id,
              email: profile?.email || 'Email inconnu',
              role_name: roleName,
              assigned_at: admin.assigned_at
            };
          });
          
          console.log('Admins avec emails et rôles (méthode de secours):', adminsWithEmail);
          setAdminsList(adminsWithEmail);
          setFilteredAdminsList(adminsWithEmail);
        }
        
        return;
      }
      
      console.log('Données des admins depuis la RPC:', data);
      
      // Si la RPC a réussi, utiliser directement les données
      if (data && data.length > 0) {
        setAdminsList(data);
        setFilteredAdminsList(data);
        
        // Récupérer les profils des admins pour les informations supplémentaires
        const userIds = data.map((admin: AdminUserItem) => admin.user_id);
        
        const { data: profilesData, error: profilesError } = await supabase
          .from('profiles')
          .select('id, mobile_money_number')
          .in('id', userIds);
        
        if (!profilesError && profilesData) {
          // Créer un objet avec les IDs comme clés pour un accès rapide
          const profilesObj: Record<string, any> = {};
          profilesData.forEach(profile => {
            profilesObj[profile.id] = profile;
          });
          
          setAdminProfiles(profilesObj);
        }
      } else {
        setAdminsList([]);
        setFilteredAdminsList([]);
      }
    } catch (err) {
      console.error('Exception lors du chargement des admins:', err);
    } finally {
      setLoadingAdmins(false);
    }
  };

  // Révoquer les droits d'un administrateur
  const revokeAdminRights = async (userId: string) => {
    if (!user || !isSuperAdmin) return;
    
    try {
      setRevokingAdmin(userId);
      
      // Ne pas permettre de révoquer ses propres droits
      if (userId === user.id) {
        alert('Vous ne pouvez pas révoquer vos propres droits d\'administrateur');
        return;
      }
      
      // Utiliser la fonction RPC pour révoquer les droits
      const { error } = await supabase
        .rpc('revoke_admin', {
          target_user_id: userId,
          admin_user_id: user.id
        });
      
      if (error) {
        console.error('Erreur lors de la révocation:', error);
        alert(`Échec de la révocation: ${error.message}`);
        return;
      }
      
      // Recharger la liste des admins
      loadAdminsList();
      
    } catch (err) {
      console.error('Exception lors de la révocation:', err);
      alert(err instanceof Error ? err.message : 'Erreur inconnue');
    } finally {
      setRevokingAdmin(null);
    }
  };
  
  // Forcer un rechargement de la liste des admins à l'affichage
  useEffect(() => {
    if (isSuperAdmin) {
      loadAdminsList();
    }
  }, [isSuperAdmin]);

  // Charger les admins au chargement si l'utilisateur est super_admin
  useEffect(() => {
    if (isSuperAdmin && !loadingAdmins && adminsList.length === 0) {
      loadAdminsList();
    }
  }, [isSuperAdmin]);

  // Recharger la liste des admins après en avoir ajouté un avec succès
  useEffect(() => {
    if (addAdminSuccess) {
      loadAdminsList();
    }
  }, [addAdminSuccess]);

  // Après le dernier useEffect pour addAdminSuccess
  // Ajouter un useEffect pour filtrer les admins
  useEffect(() => {
    if (!adminsList) {
      setFilteredAdminsList([]);
      return;
    }
    
    console.log('[AdminDashboard] Filtrage des admins avec searchTerm:', searchTerm, 'Nombre d\'admins total:', adminsList.length);
    
    if (!searchTerm.trim()) {
      setFilteredAdminsList(adminsList);
      return;
    }
    
    const term = searchTerm.toLowerCase().trim();
    const filtered = adminsList.filter(admin => {
      // Recherche par email
      if (admin.email?.toLowerCase().includes(term)) {
        return true;
      }
      
      // Recherche par numéro mobile money si disponible
      const adminProfile = adminProfiles[admin.user_id];
      if (adminProfile?.mobile_money_number && 
          adminProfile.mobile_money_number.toLowerCase().includes(term)) {
        return true;
      }
      
      return false;
    });
    
    console.log('[AdminDashboard] Résultats du filtrage:', filtered.length, 'admins trouvés');
    setFilteredAdminsList(filtered);
  }, [searchTerm, adminsList, adminProfiles]);

  // Fonction pour promouvoir un utilisateur au rang d'administrateur
  const promoteUserToAdmin = async () => {
    if (!user || !newAdminEmail) return;
    
    try {
      setAddingAdmin(true);
      setAddAdminError(null);
      setAddAdminSuccess(false);
      
      // 1. Chercher l'utilisateur par email
      const { data: userData, error: userError } = await supabase
        .from('profiles')
        .select('id, email')
        .eq('email', newAdminEmail)
        .single();
      
      if (userError) {
        console.error('Erreur lors de la recherche de l\'utilisateur:', userError);
        setAddAdminError(`Utilisateur non trouvé: ${userError.message}`);
        return;
      }
      
      if (!userData) {
        setAddAdminError('Aucun utilisateur trouvé avec cet email');
        return;
      }
      
      // 2. Vérifier si l'utilisateur est déjà admin
      const { data: existingAdmin, error: adminCheckError } = await supabase
        .from('admin_users')
        .select('*')
        .eq('user_id', userData.id);
        
      if (adminCheckError) {
        console.error('Erreur lors de la vérification admin:', adminCheckError);
      } else if (existingAdmin && existingAdmin.length > 0) {
        setAddAdminError('Cet utilisateur est déjà administrateur');
        return;
      }
      
      // 3. Promouvoir l'utilisateur
      const { error: promoteError } = await supabase
        .rpc('promote_to_admin', {
          target_user_id: userData.id,
          role_name: newAdminRole,
          admin_user_id: user.id
        });
      
      if (promoteError) {
        console.error('Erreur lors de la promotion:', promoteError);
        setAddAdminError(`Échec de la promotion: ${promoteError.message}`);
        return;
      }
      
      // Succès
      setAddAdminSuccess(true);
      setNewAdminEmail('');
      
      // Masquer le message de succès après 3 secondes
      setTimeout(() => {
        setAddAdminSuccess(false);
      }, 3000);
      
    } catch (err) {
      console.error('Exception lors de la promotion:', err);
      setAddAdminError(err instanceof Error ? err.message : 'Erreur inconnue');
    } finally {
      setAddingAdmin(false);
    }
  };

  // Fonction pour voir les statistiques d'un admin spécifique
  const viewAdminStats = async (adminId: string, adminEmail: string) => {
    if (!isSuperAdmin) return;
    
    try {
      setViewingAdminId(adminId);
      setViewingAdminEmail(adminEmail);
      setLoadingAdminStats(true);
      
      // Récupérer les informations de profil de l'admin
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('full_name, mobile_money_number, payment_status, total_payments_received, last_payment_date')
        .eq('id', adminId)
        .single();
      
      if (profileError) {
        console.error('Erreur lors de la récupération du profil:', profileError);
        setLoadingAdminStats(false);
        return;
      }
      
      // Stocker les informations de profil
      setAdminFullName(profileData?.full_name || '');
      setAdminMobileNumber(profileData?.mobile_money_number || '');
      setAdminPaymentStatus(profileData?.payment_status || 'pending');
      setAdminTotalPaymentsReceived(parseFloat(profileData?.total_payments_received) || 0);
      setAdminLastPaymentDate(profileData?.last_payment_date || null);
      
      // 1. Récupérer le nombre de parrainages
      const { count: referralCount, error: referralError } = await supabase
        .from('referrals')
        .select('*', { count: 'exact', head: true })
        .eq('admin_id', adminId);
      
      if (referralError) {
        console.error('Erreur lors du comptage des parrainages:', referralError);
        setLoadingAdminStats(false);
        return;
      }
      
      // 2. Récupérer le montant total des commissions
      const { data: earningsData, error: earningsError } = await supabase
        .from('referrals')
        .select('amount')
        .eq('admin_id', adminId)
        .eq('status', 'completed');
      
      if (earningsError) {
        console.error('Erreur lors de la récupération des commissions:', earningsError);
        setLoadingAdminStats(false);
        return;
      }
      
      // Calculer le total des commissions
      const totalEarnings = earningsData 
        ? earningsData.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0)
        : 0;
      
      setAdminReferrals(referralCount || 0);
      setAdminEarnings(totalEarnings);
      setLoadingAdminStats(false);
      
    } catch (err) {
      console.error('Erreur lors du chargement des statistiques de l\'admin:', err);
      setLoadingAdminStats(false);
    }
  };

  // Fonction pour valider le paiement d'un admin spécifique (pour super admin)
  const validateAdminPayment = async (adminId: string) => {
    if (!isSuperAdmin) return;
    
    try {
      setValidatingPayment(true);
      
      // 1. Changer le statut de pending à validated
      const { error: updateError } = await supabase
        .from('profiles')
        .update({
          payment_status: 'validated'
        })
        .eq('id', adminId);
        
      if (updateError) throw updateError;
      
      // 2. Récupérer le montant total des commissions
      const { data: earningsData, error: earningsError } = await supabase
        .from('referrals')
        .select('amount')
        .eq('admin_id', adminId)
        .eq('status', 'completed');
      
      if (earningsError) throw earningsError;
      
      const currentEarnings = earningsData 
        ? earningsData.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0)
        : 0;
      
      // 3. Enregistrer le paiement dans l'historique
      if (currentEarnings > 0) {
        const { error: logError } = await supabase
          .rpc('update_payment_info', {
            p_user_id: adminId,
            p_amount: currentEarnings,
            p_payment_date: new Date().toISOString()
          });
          
        if (logError) throw logError;
      }
      
      // 4. Remettre les statistiques à zéro
      const { error: resetError } = await supabase
        .from('referrals')
        .update({ amount: 0 })
        .eq('admin_id', adminId);
      
      if (resetError) throw resetError;
      
      // 5. Remettre le statut à pending
      const { error: statusError } = await supabase
        .from('profiles')
        .update({
          payment_status: 'pending'
        })
        .eq('id', adminId);
      
      if (statusError) throw statusError;
      
      // 6. Rafraîchir les statistiques
      await viewAdminStats(adminId, viewingAdminEmail);
      invalidateCache();
      
      setValidatingPayment(false);
      setPaymentSuccess(true);
      setTimeout(() => setPaymentSuccess(false), 3000);
    } catch (err) {
      console.error('Erreur lors de la validation du paiement:', err);
      setValidatingPayment(false);
      setPaymentError(err instanceof Error ? err.message : 'Erreur lors de la validation du paiement');
      setTimeout(() => setPaymentError(null), 3000);
    }
  };

  // Fonction pour générer/récupérer le lien de parrainage
  const getReferralLink = async () => {
    try {
      setReferralLinkLoading(true);
      
      // Récupérer le code de parrainage de l'admin
      const { data, error } = await supabase
        .from('admin_users')
        .select('referral_code')
        .eq('user_id', user?.id)
        .single();
        
      if (error) {
        console.error('Erreur lors de la récupération du code de parrainage:', error);
        throw error;
      }
      
      if (data && data.referral_code) {
        // Construire l'URL complète pour le lien de parrainage
        const host = window.location.origin;
        const referralLink = `${host}/#/signup?ref=${data.referral_code}`;
        setReferralLink(referralLink);
      } else {
        // Générer un nouveau code de parrainage via RPC
        const { data: newCodeData, error: newCodeError } = await supabase
          .rpc('generate_admin_referral_code', { admin_id: user?.id });
          
        if (newCodeError) {
          console.error('Erreur lors de la génération du code de parrainage:', newCodeError);
          throw newCodeError;
        }
        
        const host = window.location.origin;
        const referralLink = `${host}/#/signup?ref=${newCodeData}`;
        setReferralLink(referralLink);
      }
    } catch (err) {
      console.error('Exception lors de la récupération du lien de parrainage:', err);
    } finally {
      setReferralLinkLoading(false);
    }
  };

  // Charger le lien de parrainage au chargement
  useEffect(() => {
    if (!isSuperAdmin && adminRole?.name === 'admin') {
      getReferralLink();
    }
  }, [isSuperAdmin, adminRole]);

  // Fonction pour copier le lien dans le presse-papier
  const copyReferralLink = () => {
    navigator.clipboard.writeText(referralLink);
    setReferralCopied(true);
    
    // Réinitialiser l'état de copie après 2 secondes
    setTimeout(() => {
      setReferralCopied(false);
    }, 2000);
  };

  // Fonction de recherche explicite
  const handleSearch = useCallback(() => {
    console.log('[AdminDashboard] Recherche lancée avec term:', searchTerm);
    // La recherche est automatiquement effectuée grâce à l'useEffect qui observe searchTerm
    // On peut ajouter un retour visuel ici si nécessaire
  }, [searchTerm]);

  // État de chargement
  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <LoadingSpinner size="large" fullScreen={false} text={t('common.loading')} />
      </div>
    );
  }

  // Si l'utilisateur n'est pas admin (normalement géré par AdminRoute)
  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-black flex flex-col items-center justify-center p-4 text-center">
        <h1 className="text-2xl font-bold text-white mb-4">{t('admin.accessDenied.title')}</h1>
        <p className="text-gray-400 mb-6">{t('admin.accessDenied.message')}</p>
        <button
          onClick={() => navigate('/')}
          className="bg-gradient-to-r from-primary-start to-primary-end text-white px-6 py-2 rounded-xl"
        >
          {t('admin.accessDenied.returnHome')}
        </button>
      </div>
    );
  }

  // Si l'administrateur n'a aucune permission mais est un admin standard, montrer l'interface des parrainages
  if (!loading && accessibleSections.length === 0) {
    // Si c'est un admin standard, lui montrer quand même les stats de parrainage
    if (adminRole && adminRole.name === 'admin') {
      return (
        <div className="min-h-screen bg-black">
          <header className="bg-gradient-to-r from-primary-start/10 to-primary-end/10 border-b border-primary-start/20">
            <div className="container mx-auto px-4 py-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 sm:gap-0">
              <div>
                <h1 className="text-xl font-bold">
                  <span className={gradientText}>{t('admin.dashboard.title')}</span>
                </h1>
                {adminRole && (
                  <p className="text-sm text-gray-400">
                    {t('admin.dashboard.roleLabel')}: <span className="text-white">{adminRole.name}</span>
                  </p>
                )}
              </div>
              <button
                onClick={() => navigate('/profile')}
                className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
              >
                <LogOut className="w-4 h-4" />
                <span className="text-sm">{t('admin.dashboard.returnToApp')}</span>
              </button>
            </div>
          </header>
          
          <main className="container mx-auto px-4 py-4 sm:py-6">
            {/* Statistiques */}
            <div className="mb-6 sm:mb-8">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-white">{t('admin.dashboard.statsTitle')}</h2>
                <button 
                  onClick={refreshStats}
                  disabled={isLoading}
                  className="text-sm text-gray-400 hover:text-white flex items-center gap-1"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`${isLoading ? 'animate-spin' : ''}`}> 
                    <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.3"/>
                  </svg>
                  {isLoading ? t('common.loading') : t('common.refresh')}
                </button>
              </div>
              
              {error && (
                <div className="bg-primary-end/10 border border-primary-end/30 rounded-xl p-4 mb-4 text-white flex items-center gap-2">
                  <Info className="w-5 h-5 text-primary-end shrink-0" />
                  <p className="text-sm">{error}</p>
                </div>
              )}
              
              {cachedLoading ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                  {[...Array(2)].map((_, i) => (
                    <div key={i} className="bg-gray-900/50 rounded-xl p-4 sm:p-6 animate-pulse">
                      <div className="h-4 w-24 bg-gray-800 rounded mb-3"></div>
                      <div className="h-8 w-16 bg-gray-800 rounded"></div>
                    </div>
                  ))}
                </div>
              ) : stats ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                  {isSuperAdmin ? (
                    // Interface Super Admin
                    <>
                      {stats.totalUsers !== undefined && (
                        <div className="bg-gradient-to-r from-primary-start/5 to-primary-end/5 rounded-xl p-3 sm:p-4">
                          <h3 className="text-gray-400 mb-1 sm:mb-2">{t('admin.dashboard.stats.users')}</h3>
                          <p className="text-2xl font-bold text-white">{stats.totalUsers}</p>
                        </div>
                      )}
                      
                      <div className="bg-gradient-to-r from-primary-start/5 to-primary-end/5 rounded-xl p-3 sm:p-4">
                        <div className="flex justify-between items-center mb-1 sm:mb-2">
                          <h3 className="text-gray-400">Transactions totales</h3>
                          <button
                            onClick={resetTransactionsCount}
                            disabled={resettingTransactions || (stats?.payments?.totalTransactions || 0) === 0}
                            className="text-xs text-gray-400 hover:text-white flex items-center gap-1 disabled:opacity-50"
                            title="Réinitialiser le compteur de transactions"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M3 2v6h6"></path>
                              <path d="M21 12A9 9 0 0 0 6 5.3L3 8"></path>
                              <path d="M21 22v-6h-6"></path>
                              <path d="M3 12a9 9 0 0 0 15 6.7l3-2.7"></path>
                            </svg>
                            {resettingTransactions ? 'Réinitialisation...' : 'Réinitialiser'}
                          </button>
                        </div>
                        
                        <div className="flex flex-col">
                          <p className="text-2xl font-bold text-white">{stats.payments?.totalAmount?.toLocaleString() || 0} FCFA</p>
                          <p className="text-sm text-gray-400">{stats.payments?.totalTransactions || 0} transaction(s)</p>
                        </div>
                        
                        {transactionResetSuccess && (
                          <div className="mt-2 text-xs text-green-400 flex items-center">
                            <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3 mr-1" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M20 6L9 17l-5-5"></path>
                            </svg>
                            Compteur réinitialisé avec succès
                          </div>
                        )}
                        
                        {transactionResetError && (
                          <div className="mt-2 text-xs text-red-400 flex items-center">
                            <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3 mr-1" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <circle cx="12" cy="12" r="10"></circle>
                              <line x1="12" y1="8" x2="12" y2="12"></line>
                              <line x1="12" y1="16" x2="12.01" y2="16"></line>
                            </svg>
                            {transactionResetError}
                          </div>
                        )}
                      </div>
                    </>
                  ) : (
                    // Interface Admin Normal
                    <>
                      <div className="bg-gradient-to-r from-primary-start/5 to-primary-end/5 rounded-xl p-3 sm:p-4">
                        <h3 className="text-gray-400 mb-1 sm:mb-2">Nombre de parrainages</h3>
                        <p className="text-2xl font-bold text-white">{stats.totalReferrals || 0}</p>
                      </div>
                      
                      <div className="bg-gradient-to-r from-primary-start/5 to-primary-end/5 rounded-xl p-3 sm:p-4">
                        <h3 className="text-gray-400 mb-1 sm:mb-2">Revenus des parrainages</h3>
                        <p className="text-2xl font-bold text-white">{stats.referralEarnings || 0} FCFA</p>
                      </div>
                    </>
                  )}
                  
                  {Object.keys(stats).length === 0 && !error && (
                    <div className="col-span-1 sm:col-span-2 bg-gray-900/50 rounded-xl p-3 sm:p-4">
                      <p className="text-gray-400">{t('admin.dashboard.noStats')}</p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="bg-gray-900/50 rounded-xl p-3 sm:p-4">
                  <p className="text-gray-400">{t('admin.dashboard.noStats')}</p>
                </div>
              )}
            </div>
            
            {/* Profil Admin */}
            {!isSuperAdmin && adminRole?.name === 'admin' && (
              <div className="mb-6 sm:mb-8">
                <h2 className="text-lg font-semibold text-white mb-3 sm:mb-4">{t('admin.dashboard.profile.title')}</h2>
                
                <div className="bg-gradient-to-r from-primary-start/5 to-primary-end/5 rounded-xl p-4 sm:p-6">
                  {profileError && (
                    <div className="bg-red-900/20 border border-red-900/30 rounded-lg p-3 mb-4 text-sm text-red-200">
                      {profileError}
                    </div>
                  )}
                  
                  {saveSuccess && (
                    <div className="bg-green-900/20 border border-green-900/30 rounded-lg p-3 mb-4 text-sm text-green-200">
                      {t('admin.dashboard.profile.saved')}
                    </div>
                  )}
                  
                  <div className="space-y-4">
                    <div>
                      <label htmlFor="fullName" className="block text-sm text-gray-400 mb-1">
                        {t('admin.dashboard.profile.fullName')}
                      </label>
                      <input
                        id="fullName"
                        type="text"
                        value={fullName}
                        onChange={(e) => setFullName(e.target.value)}
                        className="w-full bg-gray-800/50 border border-gray-700 rounded-lg p-2 text-white"
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="mobileMoneyNumber" className="block text-sm text-gray-400 mb-1">
                        {t('admin.dashboard.profile.mobileMoneyNumber')}
                      </label>
                      <input
                        id="mobileMoneyNumber"
                        type="text"
                        value={mobileMoneyNumber}
                        onChange={(e) => setMobileMoneyNumber(e.target.value)}
                        className="w-full bg-gray-800/50 border border-gray-700 rounded-lg p-2 text-white"
                      />
                    </div>
                    
                    <div className="flex gap-3">
                      <button
                        onClick={saveProfile}
                        disabled={isSaving}
                        className="bg-gradient-to-r from-primary-start to-primary-end text-white px-4 py-2 rounded-lg disabled:opacity-50"
                      >
                        {isSaving ? t('admin.dashboard.profile.saving') : t('admin.dashboard.profile.save')}
                      </button>
                      
                      {isSuperAdmin && (
                        <button
                          onClick={validatePayment}
                          disabled={isSaving || (stats?.referralEarnings || 0) <= 0}
                          className="bg-gradient-to-r from-green-500 to-green-600 text-white px-4 py-2 rounded-lg disabled:opacity-50"
                        >
                          {isSaving ? t('admin.dashboard.profile.validating') : t('admin.dashboard.profile.validatePayment')}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Historique des paiements */}
                <div className="mt-6">
                  <h3 className="text-base font-medium text-white mb-4">{t('admin.dashboard.profile.paymentHistory')}</h3>
                  
                  <div className="bg-gradient-to-r from-primary-start/5 to-primary-end/5 rounded-xl p-4">
                    {stats?.referralEarnings !== undefined ? (
                      <>
                        <div className="mb-4">
                          <p className="text-gray-400 text-sm">
                            {t('admin.dashboard.profile.totalPayments')}: <span className="text-white font-medium">{stats.referralEarnings} FCFA</span>
                          </p>
                          <p className="text-gray-400 text-sm">
                            Total reçu à ce jour: <span className="text-white font-medium">{stats.totalPaymentsReceived || 0} FCFA</span>
                          </p>
                          <p className="text-gray-400 text-sm">
                            {t('admin.dashboard.profile.since')}: <span className="text-white">{new Date(user?.created_at || '').toLocaleDateString()}</span>
                          </p>
                        </div>
                        
                        <div className="border-t border-gray-800 pt-4">
                          <p className="text-gray-400 text-sm">
                            {t('admin.dashboard.profile.lastPayment')}:
                            <span className="text-white ml-2">
                              {stats.lastPaymentDate ? new Date(stats.lastPaymentDate).toLocaleDateString() : t('admin.dashboard.profile.noPayments')}
                            </span>
                          </p>
                        </div>
                      </>
                    ) : (
                      <p className="text-gray-400 text-sm">{t('admin.dashboard.profile.noPayments')}</p>
                    )}
                  </div>
                </div>
              </div>
            )}
            
            {/* Lien de parrainage */}
            <div className="mb-6 sm:mb-8">
              <h2 className="text-lg font-semibold text-white mb-3 sm:mb-4">Mon lien de parrainage</h2>
              
              <div className="bg-gradient-to-r from-primary-start/5 to-primary-end/5 rounded-xl p-4 sm:p-6">
                <p className="text-gray-400 mb-4 text-sm sm:text-base">
                  Partagez ce lien pour parrainer de nouveaux utilisateurs. Vous recevrez une commission sur leurs transactions.
                </p>
                
                <div className="flex flex-col sm:flex-row gap-3">
                  <div className="flex-1">
                    <input
                      type="text"
                      value={referralLink}
                      readOnly
                      className="w-full bg-gray-900/50 border border-primary-start/20 rounded-lg px-3 py-2 text-sm sm:text-base text-white"
                    />
                  </div>
                  
                  <div className="flex gap-2">
                    <button
                      onClick={copyReferralLink}
                      disabled={referralLinkLoading || !referralLink}
                      className="bg-gradient-to-r from-primary-start to-primary-end text-white px-3 sm:px-4 py-2 rounded-lg disabled:opacity-50 flex items-center justify-center whitespace-nowrap flex-1 sm:flex-auto"
                    >
                      {referralCopied ? (
                        <span className="flex items-center gap-1">
                          <Check className="w-4 h-4" />
                          <span className="sm:inline">Copié</span>
                        </span>
                      ) : (
                        <span className="flex items-center gap-1">
                          <Copy className="w-4 h-4" />
                          <span className="sm:inline">Copier</span>
                        </span>
                      )}
                    </button>
                    
                    <button
                      onClick={getReferralLink}
                      disabled={referralLinkLoading}
                      className="bg-gray-800 text-white px-3 py-2 rounded-lg disabled:opacity-50 flex items-center justify-center"
                      title="Générer un nouveau lien de parrainage"
                    >
                      {referralLinkLoading ? (
                        <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                      ) : (
                        <RefreshCw className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                </div>
                
                <p className="text-xs text-gray-500 mt-2">
                  Commission actuelle: {getReferralCommissionRate()}% sur chaque transaction
                </p>
              </div>
            </div>
          </main>
        </div>
      );
    }
    
    // Si c'est autre chose (pas de permissions du tout), afficher le message d'erreur habituel
    const repairSuperAdminPermissions = async () => {
      try {
        console.log('[AdminDashboard] Tentative de réparation des permissions super_admin');
        
        // Vérifier si l'utilisateur est bien super_admin
        const { data: roleData, error: roleError } = await supabase
          .from('admin_users')
          .select('role_id')
          .eq('user_id', user?.id)
          .single();
        
        if (roleError || !roleData) {
          console.error('[AdminDashboard] Erreur ou pas de rôle trouvé:', roleError);
          alert('Erreur: Impossible de vérifier votre rôle. ' + (roleError?.message || ''));
          return;
        }
        
        // Récupérer le nom du rôle
        const { data: adminRoleData, error: adminRoleError } = await supabase
          .from('admin_roles')
          .select('name')
          .eq('id', roleData.role_id)
          .single();
        
        if (adminRoleError || !adminRoleData) {
          console.error('[AdminDashboard] Erreur lors de la récupération du nom du rôle:', adminRoleError);
          alert('Erreur: Impossible de récupérer le nom de votre rôle. ' + (adminRoleError?.message || ''));
          return;
        }
        
        // Si pas super_admin, ne pas continuer
        if (adminRoleData.name !== 'super_admin') {
          alert('Cette réparation ne fonctionne que pour les super_admin. Votre rôle: ' + adminRoleData.name);
          return;
        }
        
        // 1. Récupérer toutes les permissions disponibles
        const { data: allPermissions, error: permissionsError } = await supabase
          .from('admin_permissions')
          .select('id');
        
        if (permissionsError || !allPermissions) {
          console.error('[AdminDashboard] Erreur lors de la récupération des permissions:', permissionsError);
          alert('Erreur: Impossible de récupérer les permissions. ' + (permissionsError?.message || ''));
          return;
        }
        
        // 2. Ajouter toutes les permissions au rôle super_admin
        const permissionsToInsert = allPermissions.map(p => ({
          role_id: roleData.role_id,
          permission_id: p.id
        }));
        
        // Supprimer d'abord les permissions existantes
        const { error: deleteError } = await supabase
          .from('admin_role_permissions')
          .delete()
          .eq('role_id', roleData.role_id);
        
        if (deleteError) {
          console.error('[AdminDashboard] Erreur lors de la suppression des permissions existantes:', deleteError);
          alert('Erreur: Impossible de supprimer les permissions existantes. ' + deleteError.message);
          return;
        }
        
        // Insérer toutes les nouvelles permissions
        const { error: insertError } = await supabase
          .from('admin_role_permissions')
          .insert(permissionsToInsert);
        
        if (insertError) {
          console.error('[AdminDashboard] Erreur lors de l\'ajout des permissions:', insertError);
          alert('Erreur: Impossible d\'ajouter les permissions. ' + insertError.message);
          return;
        }
        
        alert('Réparation réussie! ' + permissionsToInsert.length + ' permissions ajoutées. Rechargez la page.');
        window.location.reload();
      } catch (err) {
        console.error('[AdminDashboard] Erreur lors de la réparation:', err);
        alert('Une erreur est survenue lors de la réparation: ' + (err instanceof Error ? err.message : String(err)));
      }
    };
    
    return (
      <div className="min-h-screen bg-black">
        <header className="bg-gradient-to-r from-primary-start/10 to-primary-end/10 border-b border-primary-start/20">
          <div className="container mx-auto px-4 py-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 sm:gap-0">
            <div>
              <h1 className="text-xl font-bold">
                <span className={gradientText}>{t('admin.dashboard.title')}</span>
              </h1>
              {adminRole && (
                <p className="text-sm text-gray-400">
                  {t('admin.dashboard.roleLabel')}: <span className="text-white">{adminRole.name}</span>
                </p>
              )}
            </div>
            <button
              onClick={() => navigate('/profile')}
              className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
            >
              <LogOut className="w-4 h-4" />
              <span className="text-sm">{t('admin.dashboard.returnToApp')}</span>
            </button>
          </div>
        </header>
        
        <main className="container mx-auto px-4 py-6">
          <div className="bg-primary-end/10 border border-primary-end/30 rounded-xl p-6 mb-4 text-white">
            <h2 className="text-xl font-semibold mb-4">Accès limité</h2>
            <p className="mb-4">Votre compte administrateur ne dispose pas des permissions nécessaires pour visualiser ou gérer des sections. Veuillez contacter un super administrateur pour obtenir les droits d'accès appropriés.</p>
            
            <p className="text-sm text-gray-300 mt-6 mb-6">
              ID Utilisateur: {user?.id}<br />
              Rôle: {adminRole?.name || "Non défini"}
            </p>
            
            {adminRole?.name === 'super_admin' && (
              <div className="mt-6 border-t border-primary-start/20 pt-6">
                <h3 className="text-lg font-medium mb-3">Réparation des permissions</h3>
                <p className="text-sm text-gray-300 mb-4">
                  Vous êtes super_admin mais vous n'avez pas de permissions. Cliquez sur le bouton ci-dessous pour réparer automatiquement.
                </p>
                <button
                  onClick={repairSuperAdminPermissions}
                  className="bg-gradient-to-r from-primary-start to-primary-end text-white px-4 py-2 rounded-lg"
                >
                  Réparer les permissions super_admin
                </button>
              </div>
            )}
          </div>
        </main>
      </div>
    );
  }

  // Si on visualise un admin spécifique (en tant que super admin)
  if (viewingAdminId && isSuperAdmin) {
    return (
      <div className="min-h-screen bg-black">
        <header className="bg-gradient-to-r from-primary-start/10 to-primary-end/10 border-b border-primary-start/20">
          <div className="container mx-auto px-4 py-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 sm:gap-0">
            <div>
              <h1 className="text-xl font-bold">
                <span className={gradientText}>Statistiques Admin</span>
              </h1>
              <p className="text-sm text-gray-400">
                Visualisation des statistiques de <span className="text-white">{viewingAdminEmail}</span>
              </p>
            </div>
            <div className="flex items-center gap-4">
              <button
                onClick={() => setViewingAdminId(null)}
                className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                <span className="text-sm">Retour à la liste</span>
              </button>
              <button
                onClick={() => navigate('/profile')}
                className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
              >
                <LogOut className="w-4 h-4" />
                <span className="text-sm">{t('admin.dashboard.returnToApp')}</span>
              </button>
            </div>
          </div>
        </header>
        
        <main className="container mx-auto px-4 py-6">
          {/* Profil de l'admin */}
          <div className="mb-8">
            <h2 className="text-lg font-semibold text-white mb-4">Profil de l'admin</h2>
            
            <div className="bg-gradient-to-r from-primary-start/5 to-primary-end/5 rounded-xl p-4 mb-6">
              {paymentError && (
                <div className="bg-red-900/20 border border-red-900/30 rounded-lg p-3 mb-4 text-sm text-red-200">
                  {paymentError}
                </div>
              )}
              
              {paymentSuccess && (
                <div className="bg-green-900/20 border border-green-900/30 rounded-lg p-3 mb-4 text-sm text-green-200">
                  Paiement validé avec succès
                </div>
              )}
              
              <div className="space-y-3">
                <div>
                  <p className="text-gray-400 text-sm mb-1">
                    Nom complet:
                  </p>
                  <p className="text-white">
                    {loadingAdminStats ? "Chargement..." : adminFullName || "Non renseigné"}
                  </p>
                </div>
                
                <div>
                  <p className="text-gray-400 text-sm mb-1">
                    Numéro Mobile Money:
                  </p>
                  <p className="text-white">
                    {loadingAdminStats ? "Chargement..." : adminMobileNumber || "Non renseigné"}
                  </p>
                </div>
                
                <div>
                  <p className="text-gray-400 text-sm mb-1">
                    Statut de paiement:
                  </p>
                  <p className={`${adminPaymentStatus === 'validated' ? 'text-green-400' : 'text-yellow-400'}`}>
                    {loadingAdminStats ? "Chargement..." : (adminPaymentStatus === 'validated' ? 'Validé' : 'En attente')}
                  </p>
                </div>
              </div>
            </div>
            
            {/* Statistiques de l'admin */}
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-white">Statistiques de l'admin</h2>
              <button 
                onClick={() => viewAdminStats(viewingAdminId, viewingAdminEmail)}
                disabled={loadingAdminStats}
                className="text-sm text-gray-400 hover:text-white flex items-center gap-1"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`${loadingAdminStats ? 'animate-spin' : ''}`}>
                  <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.3"/>
                </svg>
                {loadingAdminStats ? t('common.loading') : t('common.refresh')}
              </button>
            </div>
            
            {loadingAdminStats ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                {[...Array(2)].map((_, i) => (
                  <div key={i} className="bg-gray-900/50 rounded-xl p-3 sm:p-4 animate-pulse">
                    <div className="h-4 w-24 bg-gray-800 rounded mb-2"></div>
                    <div className="h-8 w-16 bg-gray-800 rounded"></div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                <div className="bg-gradient-to-r from-primary-start/5 to-primary-end/5 rounded-xl p-3 sm:p-4">
                  <h3 className="text-gray-400 mb-1 sm:mb-2">Nombre de parrainages</h3>
                  <p className="text-2xl font-bold text-white">{adminReferrals}</p>
                </div>
                
                <div className="bg-gradient-to-r from-primary-start/5 to-primary-end/5 rounded-xl p-3 sm:p-4">
                  <h3 className="text-gray-400 mb-1 sm:mb-2">Revenus des parrainages</h3>
                  <p className="text-2xl font-bold text-white">{adminEarnings} FCFA</p>
                </div>
              </div>
            )}
            
            {/* Historique des paiements */}
            <div className="mt-6">
              <h3 className="text-base font-medium text-white mb-4">Historique des paiements</h3>
              
              <div className="bg-gradient-to-r from-primary-start/5 to-primary-end/5 rounded-xl p-4">
                <div className="mb-4">
                  <p className="text-gray-400 text-sm">
                    Dernier paiement:
                    <span className="text-white ml-2">
                      {adminLastPaymentDate ? new Date(adminLastPaymentDate).toLocaleDateString() : "Aucun paiement enregistré"}
                    </span>
                  </p>
                  <p className="text-gray-400 text-sm">
                    Total reçu à ce jour: <span className="text-white font-medium">{adminTotalPaymentsReceived} FCFA</span>
                  </p>
                  <p className="text-gray-400 text-sm">
                    Commission actuelle: <span className="text-white font-medium">{adminEarnings} FCFA</span>
                  </p>
                </div>
                
                <button
                  onClick={() => validateAdminPayment(viewingAdminId)}
                  disabled={validatingPayment || adminEarnings <= 0}
                  className="bg-gradient-to-r from-green-500 to-green-600 text-white px-4 py-2 rounded-lg disabled:opacity-50"
                >
                  {validatingPayment ? "Validation..." : "Valider le paiement"}
                </button>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black">
      {/* En-tête */}
      <header className="bg-gradient-to-r from-primary-start/10 to-primary-end/10 border-b border-primary-start/20">
        <div className="container mx-auto px-4 py-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 sm:gap-0">
          <div>
            <h1 className="text-xl font-bold">
              <span className={gradientText}>{t('admin.dashboard.title')}</span>
            </h1>
            {adminRole && (
              <p className="text-sm text-gray-400">
                {t('admin.dashboard.roleLabel')}: <span className="text-white">{adminRole.name}</span>
              </p>
            )}
          </div>
          <button
            onClick={() => navigate('/profile')}
            className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
          >
            <LogOut className="w-4 h-4" />
            <span className="text-sm">{t('admin.dashboard.returnToApp')}</span>
          </button>
        </div>
      </header>

      {/* Contenu principal */}
      <main className="container mx-auto px-4 py-4 sm:py-6">
        {/* Statistiques */}
        <div className="mb-6 sm:mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-white">{t('admin.dashboard.statsTitle')}</h2>
            <button 
              onClick={refreshStats}
              disabled={isLoading}
              className="text-sm text-gray-400 hover:text-white flex items-center gap-1"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`${isLoading ? 'animate-spin' : ''}`}> 
                <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.3"/>
              </svg>
              {isLoading ? t('common.loading') : t('common.refresh')}
            </button>
          </div>
          
          {error && (
            <div className="bg-primary-end/10 border border-primary-end/30 rounded-xl p-4 mb-4 text-white flex items-center gap-2">
              <Info className="w-5 h-5 text-primary-end shrink-0" />
              <p className="text-sm">{error}</p>
            </div>
          )}
          
          {cachedLoading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
              {[...Array(2)].map((_, i) => (
                <div key={i} className="bg-gray-900/50 rounded-xl p-4 sm:p-6 animate-pulse">
                  <div className="h-4 w-24 bg-gray-800 rounded mb-3"></div>
                  <div className="h-8 w-16 bg-gray-800 rounded"></div>
                </div>
              ))}
            </div>
          ) : stats ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
              {isSuperAdmin ? (
                // Interface Super Admin
                <>
                  {stats.totalUsers !== undefined && (
                    <div className="bg-gradient-to-r from-primary-start/5 to-primary-end/5 rounded-xl p-3 sm:p-4">
                      <h3 className="text-gray-400 mb-1 sm:mb-2">{t('admin.dashboard.stats.users')}</h3>
                      <p className="text-2xl font-bold text-white">{stats.totalUsers}</p>
                    </div>
                  )}
                  
                  <div className="bg-gradient-to-r from-primary-start/5 to-primary-end/5 rounded-xl p-3 sm:p-4">
                    <div className="flex justify-between items-center mb-1 sm:mb-2">
                      <h3 className="text-gray-400">Transactions totales</h3>
                      <button
                        onClick={resetTransactionsCount}
                        disabled={resettingTransactions || (stats?.payments?.totalTransactions || 0) === 0}
                        className="text-xs text-gray-400 hover:text-white flex items-center gap-1 disabled:opacity-50"
                        title="Réinitialiser le compteur de transactions"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M3 2v6h6"></path>
                          <path d="M21 12A9 9 0 0 0 6 5.3L3 8"></path>
                          <path d="M21 22v-6h-6"></path>
                          <path d="M3 12a9 9 0 0 0 15 6.7l3-2.7"></path>
                        </svg>
                        {resettingTransactions ? 'Réinitialisation...' : 'Réinitialiser'}
                      </button>
                    </div>
                    
                    <div className="flex flex-col">
                      <p className="text-2xl font-bold text-white">{stats.payments?.totalAmount?.toLocaleString() || 0} FCFA</p>
                      <p className="text-sm text-gray-400">{stats.payments?.totalTransactions || 0} transaction(s)</p>
                    </div>
                    
                    {transactionResetSuccess && (
                      <div className="mt-2 text-xs text-green-400 flex items-center">
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3 mr-1" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M20 6L9 17l-5-5"></path>
                        </svg>
                        Compteur réinitialisé avec succès
                      </div>
                    )}
                    
                    {transactionResetError && (
                      <div className="mt-2 text-xs text-red-400 flex items-center">
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3 mr-1" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="12" cy="12" r="10"></circle>
                          <line x1="12" y1="8" x2="12" y2="12"></line>
                          <line x1="12" y1="16" x2="12.01" y2="16"></line>
                        </svg>
                        {transactionResetError}
                      </div>
                    )}
                  </div>
                </>
              ) : (
                // Interface Admin Normal
                <>
                  <div className="bg-gradient-to-r from-primary-start/5 to-primary-end/5 rounded-xl p-3 sm:p-4">
                    <h3 className="text-gray-400 mb-1 sm:mb-2">Nombre de parrainages</h3>
                    <p className="text-2xl font-bold text-white">{stats.totalReferrals || 0}</p>
                  </div>
                  
                  <div className="bg-gradient-to-r from-primary-start/5 to-primary-end/5 rounded-xl p-3 sm:p-4">
                    <h3 className="text-gray-400 mb-1 sm:mb-2">Revenus des parrainages</h3>
                    <p className="text-2xl font-bold text-white">{stats.referralEarnings || 0} FCFA</p>
                  </div>
                </>
              )}
              
              {Object.keys(stats).length === 0 && !error && (
                <div className="col-span-1 sm:col-span-2 bg-gray-900/50 rounded-xl p-3 sm:p-4">
                  <p className="text-gray-400">{t('admin.dashboard.noStats')}</p>
                </div>
              )}
            </div>
          ) : (
            <div className="bg-gray-900/50 rounded-xl p-3 sm:p-4">
              <p className="text-gray-400">{t('admin.dashboard.noStats')}</p>
            </div>
          )}
        </div>

        {/* Profil Admin */}
        {!isSuperAdmin && adminRole?.name === 'admin' && (
          <div className="mb-6 sm:mb-8">
            <h2 className="text-lg font-semibold text-white mb-3 sm:mb-4">{t('admin.dashboard.profile.title')}</h2>
            
            <div className="bg-gradient-to-r from-primary-start/5 to-primary-end/5 rounded-xl p-4 sm:p-6">
              {profileError && (
                <div className="bg-red-900/20 border border-red-900/30 rounded-lg p-3 mb-4 text-sm text-red-200">
                  {profileError}
                </div>
              )}
              
              {saveSuccess && (
                <div className="bg-green-900/20 border border-green-900/30 rounded-lg p-3 mb-4 text-sm text-green-200">
                  {t('admin.dashboard.profile.saved')}
                </div>
              )}
              
              <div className="space-y-4">
                <div>
                  <label htmlFor="fullName" className="block text-sm text-gray-400 mb-1">
                    {t('admin.dashboard.profile.fullName')}
                  </label>
                  <input
                    id="fullName"
                    type="text"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    className="w-full bg-gray-800/50 border border-gray-700 rounded-lg p-2 text-white"
                  />
                </div>
                
                <div>
                  <label htmlFor="mobileMoneyNumber" className="block text-sm text-gray-400 mb-1">
                    {t('admin.dashboard.profile.mobileMoneyNumber')}
                  </label>
                  <input
                    id="mobileMoneyNumber"
                    type="text"
                    value={mobileMoneyNumber}
                    onChange={(e) => setMobileMoneyNumber(e.target.value)}
                    className="w-full bg-gray-800/50 border border-gray-700 rounded-lg p-2 text-white"
                  />
                </div>
                
                <div className="flex gap-3">
                  <button
                    onClick={saveProfile}
                    disabled={isSaving}
                    className="bg-gradient-to-r from-primary-start to-primary-end text-white px-4 py-2 rounded-lg disabled:opacity-50"
                  >
                    {isSaving ? t('admin.dashboard.profile.saving') : t('admin.dashboard.profile.save')}
                  </button>
                  
                  {isSuperAdmin && (
                    <button
                      onClick={validatePayment}
                      disabled={isSaving || (stats?.referralEarnings || 0) <= 0}
                      className="bg-gradient-to-r from-green-500 to-green-600 text-white px-4 py-2 rounded-lg disabled:opacity-50"
                    >
                      {isSaving ? t('admin.dashboard.profile.validating') : t('admin.dashboard.profile.validatePayment')}
                    </button>
                  )}
                </div>
              </div>
            </div>
            
            {/* Historique des paiements */}
            <div className="mt-6">
              <h3 className="text-base font-medium text-white mb-4">{t('admin.dashboard.profile.paymentHistory')}</h3>
              
              <div className="bg-gradient-to-r from-primary-start/5 to-primary-end/5 rounded-xl p-4">
                {stats?.referralEarnings !== undefined ? (
                  <>
                    <div className="mb-4">
                      <p className="text-gray-400 text-sm">
                        {t('admin.dashboard.profile.totalPayments')}: <span className="text-white font-medium">{stats.referralEarnings} FCFA</span>
                      </p>
                      <p className="text-gray-400 text-sm">
                        Total reçu à ce jour: <span className="text-white font-medium">{stats.totalPaymentsReceived || 0} FCFA</span>
                      </p>
                      <p className="text-gray-400 text-sm">
                        {t('admin.dashboard.profile.since')}: <span className="text-white">{new Date(user?.created_at || '').toLocaleDateString()}</span>
                      </p>
                    </div>
                    
                    <div className="border-t border-gray-800 pt-4">
                      <p className="text-gray-400 text-sm">
                        {t('admin.dashboard.profile.lastPayment')}:
                        <span className="text-white ml-2">
                          {stats.lastPaymentDate ? new Date(stats.lastPaymentDate).toLocaleDateString() : t('admin.dashboard.profile.noPayments')}
                        </span>
                      </p>
                    </div>
                  </>
                ) : (
                  <p className="text-gray-400 text-sm">{t('admin.dashboard.profile.noPayments')}</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Gestion des administrateurs (visible uniquement pour les super_admin) */}
        {isSuperAdmin && (
          <div className="mb-6 sm:mb-8">
            <h2 className="text-lg font-semibold text-white mb-3 sm:mb-4">Gestion des administrateurs</h2>
            
            {!showAddAdminForm ? (
              <button
                onClick={() => setShowAddAdminForm(true)}
                className="flex items-center gap-2 w-full bg-gradient-to-r from-primary-start/10 to-primary-end/10 border border-primary-start/30 rounded-xl p-3 sm:p-4 text-white hover:from-primary-start/20 hover:to-primary-end/20 transition-colors mb-4 sm:mb-6"
              >
                <Plus className="w-5 h-5 shrink-0" />
                <span>Ajouter un nouvel administrateur</span>
              </button>
            ) : (
              <div className="bg-gradient-to-r from-primary-start/5 to-primary-end/5 rounded-xl p-4 sm:p-6 mb-4 sm:mb-6">
                <h3 className="text-base sm:text-lg font-medium text-white mb-3 sm:mb-4">Ajouter un administrateur</h3>
                
                {addAdminError && (
                  <div className="bg-red-900/20 border border-red-900/30 rounded-lg p-3 mb-4 flex items-start gap-2 text-sm">
                    <X className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
                    <p className="text-red-200">{addAdminError}</p>
                  </div>
                )}
                
                {addAdminSuccess && (
                  <div className="bg-green-900/20 border border-green-900/30 rounded-lg p-3 mb-4 flex items-start gap-2 text-sm">
                    <Check className="w-5 h-5 text-green-500 shrink-0 mt-0.5" />
                    <p className="text-green-200">Administrateur ajouté avec succès</p>
                  </div>
                )}
                
                <div className="space-y-3 sm:space-y-4">
                  <div>
                    <label htmlFor="adminEmail" className="block text-sm text-gray-400 mb-1">
                      Email de l'utilisateur
                    </label>
                    <input
                      id="adminEmail"
                      type="email"
                      value={newAdminEmail}
                      onChange={(e) => setNewAdminEmail(e.target.value)}
                      className="w-full bg-gray-800/50 border border-gray-700 rounded-lg p-2 text-white text-sm sm:text-base"
                      placeholder="email@example.com"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="adminRole" className="block text-sm text-gray-400 mb-1">
                      Rôle
                    </label>
                    <select
                      id="adminRole"
                      value={newAdminRole}
                      onChange={(e) => setNewAdminRole(e.target.value)}
                      className="w-full bg-gray-800/50 border border-gray-700 rounded-lg p-2 text-white text-sm sm:text-base"
                    >
                      <option value="admin">Admin standard</option>
                      <option value="delegate">Délégué (lecture seule)</option>
                      <option value="super_admin">Super Admin</option>
                    </select>
                  </div>
                  
                  <div className="flex flex-wrap items-center gap-3 pt-2">
                    <button
                      onClick={promoteUserToAdmin}
                      disabled={addingAdmin || !newAdminEmail}
                      className="bg-gradient-to-r from-primary-start to-primary-end text-white px-4 py-2 rounded-lg disabled:opacity-50 text-sm sm:text-base"
                    >
                      {addingAdmin ? 'Ajout en cours...' : 'Ajouter'}
                    </button>
                    
                    <button
                      onClick={() => {
                        setShowAddAdminForm(false);
                        setNewAdminEmail('');
                        setAddAdminError(null);
                      }}
                      className="text-gray-400 hover:text-white text-sm sm:text-base"
                    >
                      Annuler
                    </button>
                  </div>
                </div>
              </div>
            )}
            
            {/* Liste des administrateurs */}
            <div className="bg-gradient-to-r from-primary-start/5 to-primary-end/5 rounded-xl relative z-10"> {/* Ajout de relative et z-10 */}
              <div className="p-4 sm:p-5 border-b border-primary-start/20">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-base sm:text-lg font-medium text-white">Liste des administrateurs</h3>
                  <button
                    onClick={loadAdminsList}
                    disabled={loadingAdmins}
                    className="text-xs sm:text-sm text-gray-400 hover:text-white flex items-center gap-1"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`${loadingAdmins ? 'animate-spin' : ''}`}>
                      <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.3"/>
                    </svg>
                    {loadingAdmins ? 'Actualisation...' : t('common.refresh')}
                  </button>
                </div>
                
                {/* Barre de recherche */}
                <div className="mt-3">
                  <div className="relative flex items-center gap-2">
                    <div className="relative flex-1">
                      <svg xmlns="http://www.w3.org/2000/svg" className="absolute top-1/2 left-3 -translate-y-1/2 w-4 h-4 text-gray-400" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="11" cy="11" r="8"></circle>
                        <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                      </svg>
                      <input
                        type="text"
                        placeholder="Rechercher un administrateur..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                        className="w-full bg-gray-800 border border-gray-700 rounded-l-lg p-2 pl-9 text-white focus:outline-none focus:border-amber-500"
                      />
                    </div>
                    <button 
                      className="bg-amber-500 hover:bg-amber-600 text-white rounded-r-lg px-4 py-2"
                      onClick={handleSearch}
                    >
                      Rechercher
                    </button>
                  </div>
                </div>
              </div>
              
              {/* Corps de la liste */}
              {loadingAdmins ? (
                <div className="p-4 sm:p-6 flex justify-center">
                  <div className="flex items-center gap-2">
                    <svg className="animate-spin h-5 w-5 text-primary-end" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span className="text-gray-400 text-sm sm:text-base">Chargement des administrateurs...</span>
                  </div>
                </div>
              ) : searchTerm ? (
                filteredAdminsList.length > 0 ? (
                  <div className="divide-y divide-primary-start/10 admin-search-results">
                    {filteredAdminsList.slice(0, 50).map(admin => (
                      <div key={admin.user_id} className="p-3 sm:p-4 flex flex-col gap-3 search-result-item">
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <Shield className={`w-4 h-4 ${
                              admin.role_name === 'super_admin' 
                                ? 'text-primary-end' 
                                : admin.role_name === 'admin' 
                                  ? 'text-blue-400' 
                                  : 'text-gray-400'
                            }`} />
                            <button 
                              onClick={() => viewAdminStats(admin.user_id, admin.email)}
                              className="text-white font-medium hover:text-primary-end transition-colors text-sm sm:text-base break-all"
                              title="Voir les statistiques de cet admin"
                            >
                              {admin.email}
                            </button>
                          </div>
                          <div className="flex items-center flex-wrap gap-2 mt-1">
                            <span className={`text-xs px-2 py-0.5 rounded-full ${
                              admin.role_name === 'super_admin' 
                                ? 'bg-primary-end/20 text-primary-end' 
                                : admin.role_name === 'admin' 
                                  ? 'bg-blue-900/20 text-blue-400' 
                                  : 'bg-gray-800 text-gray-400'
                            }`}>
                              {admin.role_name === 'super_admin' 
                                ? 'Super Admin' 
                                : admin.role_name === 'admin' 
                                  ? 'Admin' 
                                  : 'Délégué'}
                            </span>
                            <span className="text-xs text-gray-500">
                              Ajouté le {new Date(admin.assigned_at).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                        
                        {/* Ne pas afficher le bouton de révocation pour soi-même */}
                        {admin.user_id !== user?.id && (
                          <div className="flex flex-wrap items-center gap-3 mt-1 sm:mt-0 pt-1 sm:pt-0 border-t sm:border-0 border-gray-800/30">
                            <button
                              onClick={() => viewAdminStats(admin.user_id, admin.email)}
                              className="flex items-center gap-1 text-xs sm:text-sm text-blue-400 hover:text-blue-300"
                              title="Voir les statistiques"
                            >
                              <svg className="w-4 h-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                              </svg>
                              Voir stats
                            </button>
                            <button
                              onClick={() => revokeAdminRights(admin.user_id)}
                              disabled={revokingAdmin === admin.user_id}
                              className="flex items-center gap-1 text-xs sm:text-sm text-red-400 hover:text-red-300 disabled:opacity-50"
                              title="Révoquer les droits d'administrateur"
                            >
                              {revokingAdmin === admin.user_id ? (
                                <>
                                  <div className="h-4 w-4 border-2 border-red-400 border-t-transparent rounded-full animate-spin"></div>
                                  Révocation...
                                </>
                              ) : (
                                <>
                                  <UserX className="w-4 h-4" />
                                  Révoquer
                                </>
                              )}
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                    {filteredAdminsList.length > 50 && (
                      <div className="p-3 text-center text-sm text-gray-400 border-t border-primary-start/20">
                        + {filteredAdminsList.length - 50} autres résultats. Affinez votre recherche pour voir des résultats plus précis.
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="p-4 text-center">
                    <p className="text-gray-400 text-sm sm:text-base">Aucun administrateur trouvé pour cette recherche</p>
                  </div>
                )
              ) : (
                <div className="p-4 text-center">
                  <p className="text-gray-400 text-sm sm:text-base">
                    Commencez à taper pour rechercher des administrateurs...
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* CSS custom pour réduire l'espace entre éléments */}
        <style>
          {`
            /* Réduire l'espace entre la barre de recherche et les liens */
            .mb-6.sm\\:mb-8 {
              margin-bottom: 5px !important;
            }

            /* Styles pour la liste des administrateurs */
            .admin-search-results {
              position: relative;
              z-index: 1000;
              background: linear-gradient(to right, rgba(0,0,0,0.95), rgba(0,0,0,0.95));
            }

            /* Assurer que les résultats restent au-dessus */
            .admin-search-results .search-result-item {
              position: relative;
              z-index: 1001;
            }

            /* Masquer les éléments qui pourraient interférer */
            .dashboard-links {
              position: relative;
              z-index: 1;
            }
          `}
        </style>

        {/* Sections (seulement pour super_admin) */}
        {isSuperAdmin && accessibleSections.length > 0 && (
          <div className="mb-6 sm:mb-8" style={{ display: 'none' }}>
            <h2 className="text-lg font-semibold text-white mb-3 sm:mb-4">{t('admin.dashboard.sectionsTitle')}</h2>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
              {accessibleSections.map(section => (
                <div
                  key={section.id}
                  className="bg-gradient-to-r from-primary-start/5 to-primary-end/5 rounded-xl overflow-hidden"
                >
                  <button
                    onClick={() => navigate(section.path)}
                    className="w-full flex items-center p-3 sm:p-4 text-left hover:bg-gradient-to-r hover:from-primary-start/10 hover:to-primary-end/10 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      {section.icon}
                      <h3 className="text-white font-medium">{section.title}</h3>
                    </div>
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Liens principaux du tableau de bord */}
        {renderDashboardLinks()}
      </main>
    </div>
  );
};

export default AdminDashboard; 